import React, { Fragment, useState } from 'react';
import NewLayoutWithHeader from 'components/layouts/new-layout-with-header';
import { useCreateHouseholdMutation, useUpdateHouseholdMutation, useCreateMemberMutation, useGetAdministrativeDivisionsQuery } from 'store/api/baseApiSlice';
import AdministrativeDivisionSelector from './AdministrativeDivisionSelector';
import { DatePicker } from 'components/inputs';
import { formatDate } from 'lib/formatters/date';
import { PROFESSIONS } from './constants';
import { upperCase } from 'lodash';
import Box from 'components/box';
import Spinner from 'components/spinner';
import { Text } from 'components/text';

const LoadingIndicator = ({ noun, }) => (
  <Box flex justifyContent="center" alignItems="center" >
    <Fragment>
      <Box paddingRight={4} color="gray.5">
        <Spinner small />
      </Box>
      <Text verticalAlign="middle">
        {noun}
      </Text>
    </Fragment>
  </Box>
);

const AlertMessage = ({ message }) => {
  const isSuccess = message.includes('success');
  return (
    <div className={`tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-lg tw-mb-6 tw-shadow-md tw-transition-all ${
      isSuccess 
        ? 'tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-text-white' 
        : 'tw-bg-gradient-to-r tw-from-red-500 tw-to-red-600 tw-text-white'
    }`}>
      <svg 
        className={`tw-w-5 tw-h-5 tw-flex-shrink-0 ${isSuccess ? 'tw-text-green-100' : 'tw-text-red-100'}`}
        fill="currentColor" 
        viewBox="0 0 20 20"
      >
        {isSuccess ? (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        ) : (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        )}
      </svg>
      <span className="tw-font-medium">{message}</span>
    </div>
  );
};

const HouseholdForm = ({ newHousehold, handleChange, handleDateChange, selectedGote, setSelectedGote, isFetchingAdministrativeDivision, error, administrativeDivisions = [], fullNameError, ageError, nationalIdError }) => (
  <>
    {/* Administrative Division */}
    <div className="tw-mb-6">
      
      {!isFetchingAdministrativeDivision && (
        <AdministrativeDivisionSelector
          data={administrativeDivisions}
          onGoteChange={setSelectedGote}
          selectedGote={selectedGote}
          label="Administrative Division"
          required={true}
          placeholder="Select Administrative Division"
        />
      )}
    </div>

    {/* Full Name */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Full Name <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <div className="tw-relative">
        <input
          type="text"
          name="fullName"
          value={newHousehold.fullName}
          onChange={handleChange}
          className={`tw-w-full tw-pl-10 tw-pr-4 tw-h-10 tw-border tw-rounded-lg tw-transition-all tw-duration-200 tw-bg-white focus:tw-outline-none focus:tw-ring-2 ${
            fullNameError 
              ? 'tw-border-red-500 focus:tw-ring-red-200 focus:tw-border-red-500' 
              : 'tw-border-gray-300 focus:tw-ring-blue-200 focus:tw-border-blue-500 hover:tw-border-gray-400'
          }`}
          placeholder="First Middle Last (3 words, each at least 3 letters)"
          required
        />
        <svg 
          className="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
      {fullNameError && (
        <p className="tw-text-red-500 tw-text-sm tw-mt-2 tw-flex tw-items-center tw-gap-1">
          <svg className="tw-w-4 tw-h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {fullNameError}
        </p>
      )}
    </div>

    {/* Date of Birth */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Date of Birth <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <div className={ageError ? 'tw-border tw-border-red-500 tw-rounded-lg tw-p-1' : ''}>
        <DatePicker
          label="Date of Birth"
          onChange={(gregorianDate) => handleDateChange(gregorianDate)}
          defaultDate={formatDate(newHousehold.birthdate)}
          maxDate={formatDate(new Date())}
        />
      </div>
      {ageError && (
        <p className="tw-text-red-500 tw-text-sm tw-mt-2 tw-flex tw-items-center tw-gap-1">
          <svg className="tw-w-4 tw-h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {ageError}
        </p>
      )}
    </div>

    {/* Gender */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Gender <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <div className="tw-relative">
        <select
          name="gender"
          value={newHousehold.gender}
          onChange={handleChange}
          className="tw-w-full tw-pl-10 tw-pr-4 tw-h-10 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white tw-transition-all tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-200 focus:tw-border-blue-500 hover:tw-border-gray-400 tw-appearance-none tw-cursor-pointer"
          required
        >
          <option value="">Select Gender</option>
          <option value="M">Male</option>
          <option value="F">Female</option>
        </select>
        <svg 
          className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400 pointer-events-none" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <svg 
          className="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400 pointer-events-none" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </div>
    </div>

    {/* Local Membership Number */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
          </svg>
          Local Membership Number <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <div className="tw-relative">
        <input
          type="text"
          name="localMembershipNumber"
          value={newHousehold.localMembershipNumber}
          onChange={handleChange}
          className="tw-w-full tw-pl-10 tw-pr-4 tw-h-10 tw-border tw-border-gray-300 tw-rounded-lg tw-transition-all tw-duration-200 tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-200 focus:tw-border-blue-500 hover:tw-border-gray-400"
          placeholder="Enter local membership number"
          required
        />
        <svg 
          className="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      </div>
    </div>

    {/* National ID */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
          </svg>
          National ID <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <input
        type="text"
        inputMode="numeric"
        name="nationalId"
        value={newHousehold.nationalId}
        onChange={handleChange}
        maxLength={16}
        className={`tw-w-full tw-px-4 tw-h-10 tw-border tw-rounded-lg tw-font-mono tw-tracking-wider tw-transition-all tw-duration-200 tw-bg-white focus:tw-outline-none focus:tw-ring-2 ${
          nationalIdError
            ? 'tw-border-red-500 focus:tw-ring-red-200 focus:tw-border-red-500'
            : 'tw-border-gray-300 focus:tw-ring-blue-200 focus:tw-border-blue-500 hover:tw-border-gray-400'
        }`}
        placeholder="16 digits"
        required
      />
      {nationalIdError ? (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{nationalIdError}</p>
      ) : (
        <p className="tw-mt-1 tw-text-xs tw-text-gray-500">{newHousehold.nationalId.length}/16 digits</p>
      )}
    </div>

    {/* Profession */}
    <div className="tw-mb-6">
      <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Profession <span className="tw-text-red-500">*</span>
        </span>
      </label>
      <div className="tw-relative">
        <select
          name="profession"
          value={newHousehold.profession}
          onChange={handleChange}
          className="tw-w-full tw-pl-10 tw-pr-4 tw-h-10 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white tw-transition-all tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-200 focus:tw-border-blue-500 hover:tw-border-gray-400 tw-appearance-none tw-cursor-pointer"
          required
        >
          <option value="">Select Profession</option>
          {Object.values(PROFESSIONS).map((profession, index) => (
            <option key={index} value={upperCase(profession)}>
              {profession}
            </option>
          ))}
        </select>
        <svg 
          className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400 pointer-events-none" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <svg 
          className="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400 pointer-events-none" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
    </div>
  </>
);

const PhotoUploader = ({ handleChange }) => (
  <div className="tw-mb-6">
    <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
      <span className="tw-flex tw-items-center tw-gap-2">
        <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Upload Photo <span className="tw-text-gray-500 tw-font-normal">(Optional)</span>
      </span>
    </label>
    <div className="tw-relative">
      <input 
        type="file" 
        name="photo" 
        onChange={handleChange} 
        accept="image/*"
        className="tw-w-full tw-px-4 tw-py-3 tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-transition-all tw-duration-200 hover:tw-border-blue-400 hover:tw-bg-blue-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-200 focus:tw-border-blue-500 tw-cursor-pointer file:tw-mr-4 file:tw-py-2 file:tw-px-4 file:tw-rounded-lg file:tw-border-0 file:tw-text-sm file:tw-font-semibold file:tw-bg-blue-50 file:tw-text-blue-700 hover:file:tw-bg-blue-100 file:tw-cursor-pointer" 
      />
      <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
        <div className="tw-text-center tw-text-gray-500">
          <svg className="tw-w-8 tw-h-8 tw-mx-auto tw-mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span className="tw-text-sm">Click to upload or drag and drop</span>
        </div>
      </div>
    </div>
  </div>
);

const SubmitButton = ({ isCreating, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-w-full tw-px-6 tw-py-4 tw-rounded-lg tw-font-semibold tw-text-white tw-transition-all tw-duration-200 tw-shadow-lg hover:tw-shadow-xl tw-flex tw-items-center tw-justify-center tw-gap-2 ${
      isCreating 
        ? 'tw-bg-gradient-to-r tw-from-gray-400 tw-to-gray-500 tw-cursor-not-allowed' 
        : 'tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 hover:tw-from-green-600 hover:tw-to-green-700 active:tw-scale-95'
    }`}
    disabled={isCreating}
  >
    {isCreating ? (
      <>
        <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Creating Household...</span>
      </>
    ) : (
      <>
        <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span>Create Household & Add Member</span>
      </>
    )}
  </button>
);

const LoadingOverlay = ({ isFetching, isCreating, isRedirecting }) => (
  (isFetching || isCreating || isRedirecting) && (
    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-60 tw-backdrop-blur-sm tw-z-50 tw-rounded-lg">
      <div className="tw-bg-white tw-p-8 tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-min-w-[300px]">
        {isFetching && <LoadingIndicator noun="Fetching Administrative Divisions" />}
        {isCreating && <LoadingIndicator noun="Creating Household..." />}
        {isRedirecting && <LoadingIndicator noun="Redirecting..." /> }
      </div>
    </div>
  )
);

// The API reports a rejected record either as { errors: { field: [message] } } or, where the
// server shows raw exceptions, inside "exception": "#<ActiveRecord::RecordInvalid: Validation
// failed: National ID has already been taken>". Returns the plain message, or null.
const apiErrorMessage = (error) => {
  const data = (error && error.data) || {};
  const fieldErrors = Object.values(data.errors || {}).flat().filter(Boolean);
  if (fieldErrors.length) return fieldErrors.join(' ');
  const match = String(data.exception || '').match(/Validation failed: (.*?)>?$/);
  if (match) return match[1];
  return null;
};

const EnrollNewMemberForm = () => {
  const [newHousehold, setNewHousehold] = useState({
    fullName: '',
    birthdate: '',
    gender: '',
    localMembershipNumber: '',
    nationalId: '',
    profession: '',
    photo: null,
    administrativeDivisionId: '',
  });

  const [selectedGote, setSelectedGote] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [fullNameError, setFullNameError] = useState(null);
  const [ageError, setAgeError] = useState(null);
  const [nationalIdError, setNationalIdError] = useState(null);
  // A household saved by an attempt whose member was then rejected (e.g. a taken national
  // ID). There's no API to delete it, so the next attempt reuses it instead of leaving
  // another empty household behind.
  const [pendingHousehold, setPendingHousehold] = useState(null);

  const { data: administrativeDivisions = [], isLoading: isFetchingAdministrativeDivision, error } = useGetAdministrativeDivisionsQuery();
  const [createHousehold] = useCreateHouseholdMutation();
  const [updateHousehold] = useUpdateHouseholdMutation();
  const [createMember] = useCreateMemberMutation();

  // Calculate age from birthdate
  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Validate age: must be >= 18
  const validateAge = (birthdate) => {
    if (!birthdate) {
      return 'Date of Birth is required';
    }
    const age = calculateAge(birthdate);
    if (age === null) {
      return 'Date of Birth is required';
    }
    if (age < 18) {
      return `Age must be 18 or above. Current age is ${age} years.`;
    }
    return null; // Valid
  };

  // Validate full name: exactly 3 words, each at least 3 letters, only English alphabet
  const validateFullName = (fullName) => {
    if (!fullName.trim()) {
      return 'Full Name is required';
    }

    // Remove extra spaces and split by single space
    const trimmedName = fullName.trim().replace(/\s+/g, ' ');
    const words = trimmedName.split(' ');

    // Check if exactly 3 words
    if (words.length !== 3) {
      return 'Full Name must be exactly 3 words (First Middle Last)';
    }

    // Check each word
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      
      // Check if word is at least 3 letters
      if (word.length < 3) {
        return `Each word must be at least 3 letters long. "${word}" has only ${word.length} letter(s)`;
      }

      // Check if word contains only English alphabet (a-zA-Z)
      if (!/^[a-zA-Z]+$/.test(word)) {
        return `Only English alphabet letters (a-z, A-Z) are allowed. "${word}" contains invalid characters`;
      }
    }

    return null; // Valid
  };

  // Validate national ID: required for the head of household, exactly 16 digits
  // (mirrors the backend's members_national_id_16_digits check).
  const validateNationalId = (nationalId) => {
    if (!nationalId.trim()) {
      return 'National ID is required';
    }
    if (!/^\d{16}$/.test(nationalId.trim())) {
      return 'National ID must be exactly 16 digits';
    }
    return null; // Valid
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    const fullNameValidationError = validateFullName(newHousehold.fullName);
    if (fullNameValidationError) {
      errors.fullName = fullNameValidationError;
    }
    const ageValidationError = validateAge(newHousehold.birthdate);
    if (ageValidationError) {
      errors.age = ageValidationError;
    }
    if (!newHousehold.gender) errors.gender = 'Gender is required';
    if (!newHousehold.localMembershipNumber.trim())
      errors.localMembershipNumber = 'Local Membership Number is required';
    const nationalIdValidationError = validateNationalId(newHousehold.nationalId);
    if (nationalIdValidationError) {
      errors.nationalId = nationalIdValidationError;
    }
    if (!newHousehold.profession)
      errors.profession = 'Profession is required';
    if (!selectedGote)
      errors.administrativeDivisionId = 'Administrative Division is required';
    console.log(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo') {
      setNewHousehold({ ...newHousehold, photo: files[0] });
    } else if (name === 'fullName') {
      // Filter input to only allow English alphabet and spaces
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      setNewHousehold({ ...newHousehold, [name]: filteredValue });
      
      // Validate in real-time
      const error = validateFullName(filteredValue);
      setFullNameError(error);
    } else if (name === 'nationalId') {
      // Digits only, at most 16
      const digits = value.replace(/\D/g, '').slice(0, 16);
      setNewHousehold({ ...newHousehold, nationalId: digits });
      setNationalIdError(digits.length === 16 ? null : nationalIdError && validateNationalId(digits));
    } else {
      setNewHousehold({ ...newHousehold, [name]: value });
    }
  };

  const handleDateChange = (gregorianDate) => {
    setNewHousehold({ ...newHousehold, birthdate: gregorianDate });
    
    // Validate age in real-time
    const error = validateAge(gregorianDate);
    setAgeError(error);
  };

  const handleCreateHousehold = async () => {
    // Validate full name first
    const fullNameValidationError = validateFullName(newHousehold.fullName);
    if (fullNameValidationError) {
      setFullNameError(fullNameValidationError);
      setAlertMessage(fullNameValidationError);
      return;
    }

    // Validate age
    const ageValidationError = validateAge(newHousehold.birthdate);
    if (ageValidationError) {
      setAgeError(ageValidationError);
      setAlertMessage(ageValidationError);
      return;
    }

    // Checked before anything is saved: the household is created first, so a member
    // rejected afterwards would leave an empty household behind.
    const nationalIdValidationError = validateNationalId(newHousehold.nationalId);
    if (nationalIdValidationError) {
      setNationalIdError(nationalIdValidationError);
      setAlertMessage(nationalIdValidationError);
      return;
    }

    if (!validateForm()) {
      setAlertMessage('Please fill in all required fields.');
      return;
    }

    try {
      setIsCreating(true); // Set creating state
      let householdId;
      if (pendingHousehold) {
        householdId = pendingHousehold.id;
        if (pendingHousehold.divisionId !== selectedGote.value) {
          await updateHousehold({ id: householdId, administrative_division_id: selectedGote.value }).unwrap();
          setPendingHousehold({ id: householdId, divisionId: selectedGote.value });
        }
      } else {
        const householdData = {
          administrative_division_id: selectedGote.value,
          address:selectedGote.label,
          enrolled_at: new Date(),
        };
        const householdResponse = await createHousehold(householdData).unwrap();
        householdId = householdResponse.id;
        setPendingHousehold({ id: householdId, divisionId: selectedGote.value });
      }

      // Create member
      const memberData = new FormData();
      memberData.append('full_name', newHousehold.fullName);
      memberData.append(
        'birthdate',
        newHousehold.birthdate.toISOString()
      );
      memberData.append('gender', newHousehold.gender);
      memberData.append(
        'local_membership_number',
        newHousehold.localMembershipNumber
      );
      memberData.append('profession', newHousehold.profession);
      memberData.append('national_id', newHousehold.nationalId.trim());
      memberData.append('relationship_to_head', 'SELF');
      memberData.append('household_id', householdId);
      memberData.append('enrolled_at', new Date().toISOString());
      memberData.append('birthdate_accuracy', 'Y');

      if (newHousehold.photo) memberData.append('photo', newHousehold.photo);

      const memberResponse = await createMember(memberData).unwrap();
      const memberId = memberResponse.id;
      setPendingHousehold(null);

      // After success
      setAlertMessage(
        'Household and member created successfully! Redirecting...'
      );
      setIsCreating(false);
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = `/members/${memberId}/renew`;
      }, 1000);
    } catch (error) {
      setIsCreating(false);
      console.error(error);
      const message = apiErrorMessage(error);
      const raw = String((error && error.data && error.data.exception) || '');
      if (/index_members_on_full_name_and_local_membership_number/.test(raw)) {
        // A database uniqueness check, reported as a server error rather than a validation one.
        setAlertMessage('A member with this full name, gender and local membership number is already registered. Check the local membership number, or search for that member instead of enrolling them again.');
      } else if (/RecordNotUnique|duplicate key/i.test(raw)) {
        setAlertMessage('This member looks like one that is already registered. Search for them before enrolling again.');
      } else if (message && /national id has already been taken/i.test(message)) {
        const taken = 'This National ID is already registered to another member. Check the number on the ID card, or search for that member instead of enrolling them again.';
        setNationalIdError(taken);
        setAlertMessage(taken);
      } else if (message && /national id/i.test(message)) {
        setNationalIdError(message);
        setAlertMessage(message);
      } else if (message) {
        setAlertMessage(`Couldn't enroll the member: ${message}.`);
      } else if (error && error.status === 'FETCH_ERROR') {
        setAlertMessage("Couldn't reach the server. Check the internet connection and try again.");
      } else {
        setAlertMessage('Failed to create household or member. Please try again.');
      }
    }
  };

  return (
    <div className="tw-flex tw-bg-gradient-to-br tw-from-gray-50 tw-via-blue-50 tw-to-gray-100 tw-py-4 tw-px-4">
      <div className="tw-w-full tw-max-w-lg tw-mx-auto tw-bg-white tw-rounded-xl tw-shadow-2xl tw-pt-0 tw-px-8 tw-pb-8 tw-relative tw-border tw-border-gray-200 tw-overflow-hidden">
        {/* Header */}
        <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-rounded-t-xl tw-py-3 tw-px-6 tw-mb-6 tw--mx-8">
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
            <div className="tw-bg-white tw-bg-opacity-20 tw-p-1.5 tw-rounded-lg">
              <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-white">Enroll New Member</h2>
          </div>
        </div>

        {alertMessage && <AlertMessage message={alertMessage} />}

        <HouseholdForm
          newHousehold={newHousehold}
          handleChange={handleChange}
          handleDateChange={handleDateChange}
          selectedGote={selectedGote}
          setSelectedGote={setSelectedGote}
          isFetchingAdministrativeDivision={isFetchingAdministrativeDivision}
          error={error}
          administrativeDivisions={administrativeDivisions}
          fullNameError={fullNameError}
          ageError={ageError}
          nationalIdError={nationalIdError}
        />

        {/* <PhotoUploader handleChange={handleChange} /> */}

        <SubmitButton isCreating={isCreating} onClick={handleCreateHousehold} />

        <LoadingOverlay isFetching={isFetching || isFetchingAdministrativeDivision} isCreating={isCreating} isRedirecting={isRedirecting} />
      </div>
    </div>
  );
};

// Container Component with Layout
const EnrollMemberContainer = () => {
  return (
    <NewLayoutWithHeader pageTitle="Enroll Members" steps={[{ title: "Enroll Members", href: "/enroll/members" }]}>
      <EnrollNewMemberForm />
    </NewLayoutWithHeader>
  );
};

export default EnrollMemberContainer;

